import React, { useEffect, useMemo, useState } from "react";
import NotificationHubCard from "./NotificationHubCard.tsx";
import type { NotificationHubItem } from "./mapApiNotificationToHubItem.ts";
import {
  countsByHubTab,
  filterHubItemsByTab,
  hubTabsForItems,
  type HubFilterTab,
} from "./notificationHubFilters.ts";
import styles from "./NotificationHubList.module.scss";

export type { HubFilterTab };

export type NotificationHubListProps = {
  items: NotificationHubItem[];
  onMarkRead?: (id: string) => void;
  onPostulationAccept?: (item: NotificationHubItem) => void;
  onPostulationReject?: (item: NotificationHubItem) => void;
  onDelete?: (item: NotificationHubItem) => void;
};

const NotificationHubList: React.FC<NotificationHubListProps> = ({
  items,
  onMarkRead,
  onPostulationAccept,
  onPostulationReject,
  onDelete,
}) => {
  const [filter, setFilter] = useState<HubFilterTab>("all");
  const [sortBy, setSortBy] = useState<"newest" | "oldest">("newest");

  const hasAccountCategory = useMemo(
    () => items.some((i) => i.category === "account"),
    [items]
  );

  const hasSystemCategory = useMemo(
    () => items.some((i) => i.category === "system"),
    [items]
  );

  const tabLabels = useMemo(
    () => hubTabsForItems(hasAccountCategory, hasSystemCategory),
    [hasAccountCategory, hasSystemCategory]
  );

  useEffect(() => {
    if (filter === "account" && !hasAccountCategory) {
      setFilter("all");
    }
    if (filter === "system" && !hasSystemCategory) {
      setFilter("all");
    }
  }, [filter, hasAccountCategory, hasSystemCategory]);

  const filtered = useMemo(() => {
    const list = filterHubItemsByTab(items, filter);
    list.sort((a, b) => {
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return sortBy === "newest" ? tb - ta : ta - tb;
    });
    return list;
  }, [items, filter, sortBy]);

  const totalUnread = useMemo(
    () => items.filter((i) => !i.read).length,
    [items]
  );

  const countsByTab = useMemo(() => countsByHubTab(items), [items]);

  return (
    <div>
      <div className={styles.toolbar}>
        <div className={styles.tabRow}>
          {tabLabels.map((tab) => {
            const n = countsByTab[tab.id];
            const label =
              n > 0 ? `${tab.label} (${n})` : tab.label;
            return (
            <button
              key={tab.id}
              type="button"
              className={`${styles.tab} ${filter === tab.id ? styles.tabActive : ""}`}
              onClick={() => setFilter(tab.id)}
            >
              {label}
            </button>
            );
          })}
        </div>
        <div>
          <span className={styles.sortLabel}>Orden</span>
          <select
            className={styles.sortSelect}
            value={sortBy}
            onChange={(e) =>
              setSortBy(e.target.value as "newest" | "oldest")
            }
          >
            <option value="newest">Más recientes</option>
            <option value="oldest">Más antiguas</option>
          </select>
        </div>
      </div>
      <p className={styles.summary}>
        Mostrando {filtered.length} de {items.length}
        {totalUnread > 0 ? ` · ${totalUnread} sin leer` : ""}
      </p>
      <div className={styles.list}>
        {filtered.length === 0 ? (
          <div className={styles.empty}>
            No hay notificaciones en este filtro.
          </div>
        ) : (
          filtered.map((item) => (
            <NotificationHubCard
              key={item.id || item.type + item.createdAt}
              item={item}
              onMarkRead={onMarkRead}
              onPostulationAccept={onPostulationAccept}
              onPostulationReject={onPostulationReject}
              onDelete={onDelete}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default NotificationHubList;
